import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  Radio,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Payments as PaymentsIcon,
  Undo as UndoIcon,
  MonetizationOn as MonetizationOnIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon,
  Star as StarIcon,
  ShoppingCart as ShoppingCartIcon,
  Group as GroupIcon,
  AccountCircle as AccountCircleIcon,
  TrendingUp as TrendingUpIcon,
  Close as CloseIcon,
  FilterAlt as FilterAltIcon,
  FileDownload as FileDownloadIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarMonthIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { getDayBookReport } from '../../services/reports';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import ExportButton from '../../components/ExportButton/ExportButton';
import FormatBadge from '../../components/FormatBadge/FormatBadge';
import AlertModal from '../../components/AlertModal';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';
import { showSnackbar } from '../../components/Snackbar';

const AdminPayment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dayBookData, setDayBookData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filters state
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('');

  // Export Dialog States
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('');
  const [exportDateMode, setExportDateMode] = useState('current');
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const closeAlert = () => setAlertModal({ open: false, title: 'Alert', message: '' });

  const handleOpenExportDialog = (type) => {
    setExportType(type);
    setExportDialogOpen(true);
    setExportFromDate(fromDate || '');
    setExportToDate(toDate || '');
    setExportDateMode('current');
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
    setExportType('');
    setExportLoading(false);
  };

  const handleConfirmExport = async () => {
    setExportLoading(true);
    try {
      const exportParams = {
        export: 'true'
      };

      if (exportDateMode !== 'all') {
        if (exportDateMode === 'current') {
          if (fromDate) exportParams.fromDate = fromDate;
          if (toDate) exportParams.toDate = toDate;
        } else if (exportDateMode === 'custom') {
          if (exportFromDate) exportParams.fromDate = exportFromDate;
          if (exportToDate) exportParams.toDate = exportToDate;
        } else if (exportDateMode === 'single') {
          if (exportFromDate) {
            exportParams.fromDate = exportFromDate;
            exportParams.toDate = exportFromDate;
          }
        }
      }

      if (searchTerm.trim()) {
        exportParams.search = searchTerm.trim();
      }
      if (type && type !== 'all') {
        exportParams.type = type;
      }
      if (status) {
        exportParams.status = status;
      }

      const response = await getDayBookReport(exportParams);
      const docs = response && (response.status === true || response.success)
        ? (Array.isArray(response.data?.docs) ? response.data.docs : (Array.isArray(response.data) ? response.data : []))
        : [];

      if (docs.length === 0) {
        setAlertModal({
          open: true,
          title: 'Warning',
          message: 'No data found for the selected filter/date range.'
        });
        setExportLoading(false);
        return;
      }

      const mappedExport = docs.map((item, index) => {
        const dateSource = item.receipt?.receiptDate || item.createdAt;
        const formattedDate = dateSource ? new Date(dateSource).toLocaleDateString('en-IN') : 'N/A';
        const associateName = item.targetAssociate?.name || item.sellerAssociate?.name || '—';
        const customerName = item.customer?.name || '—';
        let projectDetails = '—';
        if (item.project && item.project.name) {
          projectDetails = item.project.name;
          if (item.project.unit) {
            projectDetails += ` (${item.project.unit})`;
          }
        }
        const refNo = item.receipt?.receiptNo || item.booking?.bookingNo || '—';
        const displayType = item.type
          ? item.type.charAt(0).toUpperCase() + item.type.slice(1).replace('_', ' ')
          : '—';

        let displayAmountLabel = '-';
        let displayAmountValue = 0;

        if (item.type === "booking") {
          displayAmountLabel = "Booking Amount";
          displayAmountValue = item.amounts?.bookingAmount || 0;
        } else if (item.type === "receipt") {
          displayAmountLabel = "Received";
          displayAmountValue = item.amounts?.receiptAmount || 0;
        } else if (item.type === "refund") {
          displayAmountLabel = "Refund";
          displayAmountValue = item.amounts?.receiptAmount || 0;
        } else if (item.type === "commission") {
          displayAmountLabel = "Commission Generated";
          displayAmountValue = item.amounts?.payableCommission || 0;
        } else if (item.type === "associate_ledger") {
          displayAmountLabel = item.receipt?.receivedType || "Ledger Amount";
          displayAmountValue = item.amounts?.receiptAmount || 0;
        } else if (item.type === "incentive") {
          displayAmountLabel = item.action || "Incentive";
          displayAmountValue = item.amounts?.payableCommission || item.amounts?.incentiveAmount || item.amounts?.reversedAmount || 0;
        } else if (item.type === "purchase") {
          displayAmountLabel = "Purchase Amount";
          displayAmountValue = item.amounts?.purchaseAmount || 0;
        } else if (item.type === "seller_ledger") {
          displayAmountLabel = "Seller Payment";
          displayAmountValue = item.amounts?.receiptAmount || 0;
        }

        const displayStatus = item.status === 'add' ? 'Credit' : item.status === 'deduct' ? 'Debit' : item.status || '—';

        return {
          "Sr.No.": index + 1,
          "Date": formattedDate,
          "Type": displayType,
          "Ref": refNo,
          "Party": customerName,
          "Associate": associateName,
          "Project/Unit": projectDetails,
          "Amount": displayAmountValue,
          "Amount Type": displayAmountLabel,
          "Status": displayStatus
        };
      });

      const fileName = `Admin_DayBookReport_${new Date().toLocaleDateString("en-IN").replace(/\//g, "-")}`;

      if (exportType === 'csv') {
        exportAsCSV({ rows: mappedExport, fileNameBase: fileName });
      } else if (exportType === 'pdf') {
        exportAsPDF({ rows: mappedExport, title: "Day Book Ledger Report", fileNameBase: fileName });
      } else if (exportType === 'excel') {
        exportAsExcel({ rows: mappedExport, sheetName: "Day Book Ledger Report", fileNameBase: fileName });
      }

      handleCloseExportDialog();
    } catch (err) {
      console.error('Export failed:', err);
      showSnackbar({ type: 'error', message: 'Export failed. Please try again.' });
    } finally {
      setExportLoading(false);
    }
  };

  // Fetch Day Book data
  const fetchDayBookData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (fromDate) {
        params.fromDate = fromDate;
      }
      if (toDate) {
        params.toDate = toDate;
      }
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      if (type && type !== 'all') {
        params.type = type;
      }
      if (status) {
        params.status = status;
      }

      const response = await getDayBookReport(params);
      console.log('Day Book API Full Response:', response);

      if (response && (response.status === true || response.success)) {
        // Support dynamic structures (docs inside data, or directly inside data)
        const docs = Array.isArray(response.data?.docs)
          ? response.data.docs
          : Array.isArray(response.data)
            ? response.data
            : [];

        const count = response.data?.totalDocs || response.total || response.totalCount || docs.length;

        const mappedDocs = docs.map((item, index) => {
          // Date extraction: receiptDate or createdAt
          const dateSource = item.receipt?.receiptDate || item.createdAt;
          const formattedDate = dateSource ? new Date(dateSource).toLocaleDateString('en-IN') : 'N/A';

          // Associate extraction: targetAssociate or sellerAssociate
          const associateName = item.targetAssociate?.name || item.sellerAssociate?.name || '—';

          // Customer extraction
          const customerName = item.customer?.name || '—';

          // Project & Unit details
          let projectDetails = '—';
          if (item.project && item.project.name) {
            projectDetails = item.project.name;
            if (item.project.unit) {
              projectDetails += ` (${item.project.unit})`;
            }
          }

          // Ref
          const refNo = item.receipt?.receiptNo || item.booking?.bookingNo || '—';

          // Type formatting
          const displayType = item.type
            ? item.type.charAt(0).toUpperCase() + item.type.slice(1).replace('_', ' ')
            : '—';

          // Frontend amount pick rules as specified
          let displayAmountLabel = '-';
          let displayAmountValue = 0;

          if (item.type === "booking") {
            displayAmountLabel = "Booking Amount";
            displayAmountValue = item.amounts?.bookingAmount || 0;
          } else if (item.type === "receipt") {
            displayAmountLabel = "Received";
            displayAmountValue = item.amounts?.receiptAmount || 0;
          } else if (item.type === "refund") {
            displayAmountLabel = "Refund";
            displayAmountValue = item.amounts?.receiptAmount || 0;
          } else if (item.type === "commission") {
            displayAmountLabel = "Commission Generated";
            displayAmountValue = item.amounts?.payableCommission || 0;
          } else if (item.type === "associate_ledger") {
            displayAmountLabel = item.receipt?.receivedType || "Ledger Amount";
            displayAmountValue = item.amounts?.receiptAmount || 0;
          } else if (item.type === "incentive") {
            displayAmountLabel = item.action || "Incentive";
            displayAmountValue = item.amounts?.payableCommission || item.amounts?.incentiveAmount || item.amounts?.reversedAmount || 0;
          } else if (item.type === "purchase") {
            displayAmountLabel = "Purchase Amount";
            displayAmountValue = item.amounts?.purchaseAmount || 0;
          } else if (item.type === "seller_ledger") {
            displayAmountLabel = "Seller Payment";
            displayAmountValue = item.amounts?.receiptAmount || 0;
          }

          // Status mapping
          const displayStatus = item.status === 'add' ? 'Credit' : item.status === 'deduct' ? 'Debit' : item.status || '—';

          return {
            sno: page * rowsPerPage + index + 1,
            id: item.id || item._id || index + 1,
            transactionDate: formattedDate,
            type: displayType,
            ref: refNo,
            associate: associateName,
            customer: customerName,
            projectUnit: projectDetails,
            amount: displayAmountValue,
            amountType: displayAmountLabel,
            status: displayStatus,
          };
        });

        setDayBookData(mappedDocs);
        setTotalCount(count);
        setSummary(response.data?.summary || response.summary || null);
      } else {
        setDayBookData([]);
        setTotalCount(0);
        setSummary(null);
      }
    } catch (err) {
      console.error('Failed to load Day Book data:', err);
      setError(err?.message || 'Failed to fetch Day Book report from server.');
      setDayBookData([]);
      setTotalCount(0);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, fromDate, toDate, searchTerm, type, status]);

  useEffect(() => {
    fetchDayBookData();
  }, [fetchDayBookData]);

  const handleSearch = () => {
    setPage(0);
    fetchDayBookData();
  };

  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setSearchTerm('');
    setType('all');
    setStatus('');
    setPage(0);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Day Book
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Real-time credit and debit ledger report
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <ExportButton
            onExportPdf={() => handleOpenExportDialog('pdf')}
            onExportCsv={() => handleOpenExportDialog('csv')}
            onExportExcel={() => handleOpenExportDialog('excel')}
          />
          <RefreshButton onClick={fetchDayBookData} loading={loading} />
        </Box>
      </Box>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[
            {
              key: 'booking',
              label: 'Bookings',
              amount: summary.totalBookingAmount,
              color: '#1976d2',
              bgLight: 'rgba(25, 118, 210, 0.05)',
              icon: <AssessmentIcon sx={{ fontSize: 18 }} />,
            },
            {
              key: 'receipt',
              label: 'Receipts',
              amount: summary.totalReceiptAmount,
              color: '#2e7d32',
              bgLight: 'rgba(46, 125, 50, 0.05)',
              icon: <ReceiptIcon sx={{ fontSize: 18 }} />,
            },
            {
              key: 'refund',
              label: 'Refunds',
              amount: summary.totalRefundAmount,
              color: '#d32f2f',
              bgLight: 'rgba(211, 47, 47, 0.05)',
              icon: <UndoIcon sx={{ fontSize: 18 }} />,
            },
            {
              key: 'commission',
              label: 'Commissions',
              amount: summary.totalPayableCommission,
              color: '#ed6c02',
              bgLight: 'rgba(237, 108, 2, 0.05)',
              icon: <PaymentsIcon sx={{ fontSize: 18 }} />,
              subtext: `Paid: ${formatCurrency(summary.totalPaidCommission)}`,
            },
            {
              key: 'associate_ledger',
              label: 'Associate Ledger',
              amount: summary.totalAssociateLedgerAmount,
              color: '#9c27b0',
              bgLight: 'rgba(156, 39, 176, 0.05)',
              icon: <GroupIcon sx={{ fontSize: 18 }} />,
            },
            {
              key: 'incentive',
              label: 'Incentives',
              amount: summary.totalIncentiveAmount,
              color: '#00796b',
              bgLight: 'rgba(0, 121, 107, 0.05)',
              icon: <TrendingUpIcon sx={{ fontSize: 18 }} />,
              subtext: `Paid: ${formatCurrency(summary.totalIncentivePaidAmount)}`,
            },
            {
              key: 'purchase',
              label: 'Purchases',
              amount: summary.totalPurchaseAmount,
              color: '#7b1fa2',
              bgLight: 'rgba(123, 31, 162, 0.05)',
              icon: <ShoppingCartIcon sx={{ fontSize: 18 }} />,
              subtext: `Paid: ${formatCurrency(summary.totalPurchasePaidAmount)}`,
            },
          ].filter((card) => (card.amount || 0) > 0).map((card) => {
            return (
              <Grid item xs={6} sm={4} md={3} lg={2.4} key={card.key} sx={{ display: 'flex' }}>
                <Card
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                    borderRadius: 2,
                    p: 0.5,
                    cursor: 'default',
                    bgcolor: '#fff',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    },
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '8px 10px !important', height: '100%', gap: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: '0.65rem', letterSpacing: 0.3, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {card.label}
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.1, color: 'text.primary', fontSize: '0.85rem' }}>
                        {formatCurrency(card.amount)}
                      </Typography>
                      {card.subtext ? (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.2, fontSize: '0.62rem', fontWeight: 500 }}>
                          {card.subtext}
                        </Typography>
                      ) : (
                        <Box sx={{ height: '12px', mt: 0.2 }} />
                      )}
                    </Box>
                    <Box sx={{ p: 0.6, borderRadius: '50%', bgcolor: card.bgLight, color: card.color, display: 'flex', flexShrink: 0 }}>
                      {card.icon}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Filters Section */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>
            Filter Ledger Entries
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="booking">Booking</MenuItem>
                  <MenuItem value="receipt">Receipt</MenuItem>
                  <MenuItem value="refund">Refund</MenuItem>
                  <MenuItem value="commission">Commission</MenuItem>
                  <MenuItem value="purchase">Purchase</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="booked">Booked</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="reversed">Reversed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={9}>
              <TextField
                fullWidth
                size="small"
                label="Search Details"
                placeholder="Search by action, associate, customer, etc..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading}
                  sx={{ height: 40, textTransform: 'none', fontWeight: 600 }}
                >
                  Search
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ height: 40, textTransform: 'none', fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table Section */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)', borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Ledger Entries
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#536DFE' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>S. No.</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Ref</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Party</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Associate</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Project/Unit</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }} align="right">Amount</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Amount Type</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          Fetching Day Book entries...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : dayBookData.length > 0 ? (
                  dayBookData.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#fcfcfc' },
                        '&:hover': { backgroundColor: '#f0f7ff !important' },
                      }}
                    >
                      <TableCell>{row.sno}</TableCell>
                      <TableCell>{row.transactionDate}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.type}</TableCell>
                      <TableCell>{row.ref}</TableCell>
                      <TableCell>{row.customer}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.associate}</TableCell>
                      <TableCell>{row.projectUnit}</TableCell>
                      <TableCell align="right" sx={{
                        fontWeight: 700,
                        color: 'text.primary'
                      }}>
                        {formatCurrency(row.amount)}
                      </TableCell>
                      <TableCell sx={{ fontStyle: 'italic', color: 'text.secondary' }}>{row.amountType}</TableCell>
                      <TableCell sx={{
                        fontWeight: 600,
                        color: (() => {
                          const s = (row.status || '').toLowerCase();
                          if (s.includes('debit') || s.includes('deduct')) return 'error.main';
                          if (s.includes('reverse')) return 'warning.main';
                          if (s.includes('approve') || s === 'credit' || s.includes('booked')) return 'success.main';
                          if (s.includes('payable')) return '#9c27b0'; // Distinct Purple for Payable
                          return 'text.primary';
                        })()
                      }}>
                        {row.status}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <Typography variant="body1" color="text.secondary">
                        No ledger entries found matching your criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalCount > 0 && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ mt: 2 }}
            />
          )}
        </CardContent>
      </Card>

      {/* Export Date Range Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 16px 48px rgba(83, 109, 254, 0.15), 0 2px 12px rgba(0,0,0,0.06)',
          }
        }}
      >
        {/* Gradient Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 50%, #304ffe 100%)',
          px: 2.5, py: 1.8,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 55, height: 55, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileDownloadIcon sx={{ color: '#fff', fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', lineHeight: 1.2 }}>
                  Export Day Book Ledger
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', mt: 0.2 }}>
                  Download your report data
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseExportDialog} size="small" sx={{ color: 'rgba(255,255,255,0.8)', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          {/* Format Badge */}
          <FormatBadge type={exportType} />

          {/* Section label */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Select Date Range
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {/* Option 1: Current Filters */}
            <Box
              onClick={() => setExportDateMode('current')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'current' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'current' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'current' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <FilterAltIcon sx={{ fontSize: 16, color: exportDateMode === 'current' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Current Filters
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {fromDate && toDate
                    ? `${fromDate} → ${toDate}`
                    : fromDate ? `From ${fromDate}` : toDate ? `Till ${toDate}` : 'All dates'}
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'current'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 2: Custom Date Range */}
            <Box
              onClick={() => setExportDateMode('custom')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'custom' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'custom' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'custom' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <DateRangeIcon sx={{ fontSize: 16, color: exportDateMode === 'custom' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Custom Date Range
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Choose from & to dates
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'custom'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 3: Single Day */}
            <Box
              onClick={() => setExportDateMode('single')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'single' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'single' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'single' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <TodayIcon sx={{ fontSize: 16, color: exportDateMode === 'single' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Single Day
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export for one specific date
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'single'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 4: All Dates */}
            <Box
              onClick={() => setExportDateMode('all')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'all' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'all' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'all' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <CalendarMonthIcon sx={{ fontSize: 16, color: exportDateMode === 'all' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  All Dates (Full History)
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export full database history without any filters
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'all'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>
          </Box>

          {/* Conditional Input Fields */}
          {exportDateMode === 'custom' && (
            <Box sx={{
              display: 'flex', gap: 1.5, mt: 1.5,
              p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="From Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
              <TextField
                label="To Date" type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}

          {exportDateMode === 'single' && (
            <Box sx={{
              mt: 1.5, p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="Select Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 3, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleCloseExportDialog}
              sx={{ textTransform: 'none', fontWeight: 600 }}
              disabled={exportLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmExport}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#536DFE',
                '&:hover': { bgcolor: '#3d5afe' }
              }}
              disabled={exportLoading}
            >
              {exportLoading ? 'Exporting...' : 'Export'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Warning/Alert Modal */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        onClose={closeAlert}
      />
    </Box>
  );
};

export default AdminPayment;
